import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { useContext } from "react";
import { AuthContext } from "../../context/auth-context/AuthContext";
import { authReducerActionType } from "../../types/globalTypes";
import { showErrorMessage, showSuccessMessage } from "../../utils/toastUtils";
import { InternalProjectDto } from "../../types/traineesTypes";
import { getIP, updateIP, deleteIP } from "../../api/internalProjects";

/**
 * Hook that handles the internal project details api calls, uses react-query
 * @param internalProjectID - The internal project ID
 * @returns The internal project details, a function to update the internal project details and a function to delete the internal project
 */
const useInternalProjectDetails = (internalProjectID: number) => {
  const { dispatchAuth } = useContext(AuthContext);
  const queryClient = useQueryClient();

  // ############
  // GET QUERIES
  // ############

  // getInternalProjectDetails query
  const { data: internalProjectDetailsData, isFetched: isInternalProjectDetailsFetched } = useQuery({
    queryKey: ["internalProjectDetails", internalProjectID],
    queryFn: () => getIP(internalProjectID),
  });

  // The dates are converted to dayjs objects and the members are formatted to also include a name
  const internalProjectDetails = internalProjectDetailsData?.data;

  // ----------------------------------------------------------------------------------

  // ############
  // UPDATE QUERIES
  // ############

  // updateInternalProjectDetails mutation
  const { mutateAsync: mutateInternalProjectDetails } = useMutation({
    mutationFn: updateIP,
    onError: (err: AxiosError) => {
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
      if (axios.isCancel(err)) {
        // TODO: Handle cancel error!!!
        // eslint-disable-next-line no-console
        console.log("Request canceled", err.message);
      } else if ((err as AxiosError)?.code === "ECONNABORTED") {
        // TODO: Handle timeout error!!!
        // eslint-disable-next-line no-console
        console.log("Timeout error", (err as AxiosError)?.message);
      }
      showErrorMessage("Aktualisierung ist fehlgeschlagen!");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["internalProjectDetails", internalProjectID] });
      showSuccessMessage("Aktualisierung erfolgreich!");
    },
  });

  /**
   * Updates the internal project details
   * @param internalProjectDetails - The internal project details to update
   * @returns The updated internal project details
   */
  const updateInternalProjectDetails = async (internalProjectDetails: InternalProjectDto) => {
    return await mutateInternalProjectDetails(internalProjectDetails);
  };

  // ----------------------------------------------------------------------------------
  // ############
  // DELETE MUTATIONS
  // ############

  // deleteInternalProject mutation
  const { mutateAsync: mutateDeleteInternalProject } = useMutation({
    mutationFn: deleteIP,
    onError: (err: AxiosError) => {
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
      showErrorMessage("Löschen des IPs ist fehlgeschlagen!");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["internalProjects"] });
      showSuccessMessage("Löschen des IPs erfolgreich!");
    },
  });

  /**
   * Deletes the internal project
   * @param internalProjectId - The ID of the internal project to delete
   * @returns void
   */
  const deleteInternalProject = async (internalProjectId: number) => {
    return await mutateDeleteInternalProject(internalProjectId);
  };

  return {
    internalProjectDetails,
    isInternalProjectDetailsFetched,
    updateInternalProjectDetails,
    deleteInternalProject,
  };
};

export default useInternalProjectDetails;
